import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Card } from '@fs/zion-ui'
import AdditionalFactsHeader from './components/AdditionalFactsHeader'
import AdditionalFactsCardEditMode from './AdditionalFactsCardEditMode'
import AdditionalFactsReviewList from './components/AdditionalFactsReviewList'
import { filterOutTheseFactTypes, isValidAdditionalFactType } from './utils/additionalFacts.utils'

/**
 * AdditionalFactsCard component displays and manages additional facts for a person record.
 * It supports both edit and review modes, allowing users to view and modify editable facts
 * associated with a person.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.person - Person object containing record data and sub-elements
 * @param {string} props.person.id - Unique identifier for the person
 * @param {string} props.person.recordId - Record identifier for the person
 * @param {Array} [props.person.subElements] - Array of sub-elements containing additional facts
 * @param {Function} props.onSave - Callback function invoked when saving changes
 * @param {boolean} [props.editStep=false] - Whether the card is in edit step mode
 * @param {Function} [props.onDirtyChange] - Callback function invoked when the dirty state changes
 * @param {boolean} [props.saveCardDataToSls=false] - Whether to save card data to SLS
 * @param {string}   [props.splitScreenPartitionKey]        Split screen partition key if using split screen.
 * @returns {React.ReactElement|null} The rendered component or null if required props are missing
 */
const AdditionalFactsCard = ({
  person,
  onSave,
  editMode = false,
  setEditMode,
  onDirtyChange,
  saveCardDataToSls = false,
  reviewed = false,
  setReviewed = () => {},
  splitScreenPartitionKey,
}) => {
  const additionalFacts = useMemo(
    () =>
      person?.subElements?.filter(
        (se) =>
          se.elementType === 'FIELD' &&
          !filterOutTheseFactTypes.includes(se.fieldType) &&
          isValidAdditionalFactType(se?.typeData)
      ) || [],
    [person]
  )

  if (!person || !onSave) {
    console.error('AdditionalFactsCard: Missing required props "person" or "onSave"')
    return null
  }

  return (
    <Card margins="xxs" edges={editMode ? 'elevation' : 'border'}>
      <AdditionalFactsHeader editMode={editMode} reviewed={reviewed} setEditMode={setEditMode} />
      {reviewed && !editMode && <AdditionalFactsReviewList additionalFacts={additionalFacts} />}
      {editMode && (
        <AdditionalFactsCardEditMode
          person={person}
          reviewed={reviewed}
          onSave={onSave}
          setReviewed={setReviewed}
          setEditMode={setEditMode}
          additionalFacts={additionalFacts}
          onDirtyChange={onDirtyChange}
          saveCardDataToSls={saveCardDataToSls}
          splitScreenPartitionKey={splitScreenPartitionKey}
        />
      )}
    </Card>
  )
}

AdditionalFactsCard.propTypes = {
  person: PropTypes.shape({
    id: PropTypes.string.isRequired,
    recordId: PropTypes.string.isRequired,
  }).isRequired,
  onSave: PropTypes.func.isRequired,
  editMode: PropTypes.bool,
  setEditMode: PropTypes.func,
  onDirtyChange: PropTypes.func,
  saveCardDataToSls: PropTypes.bool,
  reviewed: PropTypes.bool,
  setReviewed: PropTypes.func,
  splitScreenPartitionKey: PropTypes.string,
}

export default AdditionalFactsCard
